import { useState, type FormEvent } from 'react';
import { X } from 'lucide-react';
import { inviteMember } from '../../hooks/useBands';

interface Props {
  bandId: string;
  invitedBy: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function InviteMemberModal({ bandId, invitedBy, onClose, onSuccess }: Props) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');

    const { error } = await inviteMember(bandId, email, role, invitedBy);
    if (error) {
      setError(error);
      setLoading(false);
    } else {
      onSuccess();
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <h2 className="text-lg font-semibold">Invite Band Member</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-50">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="bg-red-900/50 border border-red-700 text-red-200 px-3 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Email Address *</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="bandmate@example.com"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              If they already have a Nokset account, they'll be added automatically.
              Otherwise, they'll receive an invitation email.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'admin' | 'member')}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="member">Member - Can view and add events</option>
              <option value="admin">Admin - Can manage band settings and members</option>
            </select>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-50 py-2 px-4 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-2 px-4 rounded-lg transition"
            >
              {loading ? 'Inviting...' : 'Send Invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
